// Push Notifications Manager
// Handles push notification subscriptions and VAPID key management

import Cookies from 'js-cookie';
import { notificationsAPI } from '../services/api';

/**
 * Push Notification Manager
 * Manages push notification subscriptions, permissions, and VAPID keys
 */
class PushNotificationManager {
  constructor() {
    this.registration = null;
    this.subscription = null;
    this.permissionState = 'default';
    this.vapidPublicKey = null;
    this.isInitialized = false;
    this.subscriptionPromise = null; // Track ongoing subscription operations
    this.lastSubscriptionCheck = 0; // Prevent too frequent checks

    // Initialize
    this.init();
  }

  /**
   * Initialize push notification manager
   */
  async init() {
    // Get service worker registration
    if ('serviceWorker' in navigator) {
      try {
        this.registration = await navigator.serviceWorker.ready;
        console.log('[Push Manager] Service worker ready');
      } catch (error) {
        console.error('[Push Manager] Failed to get service worker registration:', error);
      }
    }

    // Check current permission state
    this.permissionState = await this.getPermissionState();

    // Load VAPID public key
    await this.loadVapidKey();

    // Get existing subscription if any
    await this.getExistingSubscription();

    this.isInitialized = true;
  }

  /**
   * Load VAPID public key from backend
   */
  async loadVapidKey() {
    try {
      // Use axios-based API call
      const data = await notificationsAPI.getVapidPublicKey();
      this.vapidPublicKey = data.public_key;

      if (!this.vapidPublicKey) {
        throw new Error('VAPID public key is not set');
      }

      console.log('[Push Manager] VAPID key loaded:', `${this.vapidPublicKey.substring(0, 10)}...`);
    } catch (error) {
      console.error('[Push Manager] Failed to load VAPID key:', error);
    }
  }

  /**
   * Get current permission state
   */
  async getPermissionState() {
    if (!('Notification' in window)) {
      return 'unsupported';
    }

    if ('permissions' in navigator) {
      try {
        const permission = await navigator.permissions.query({ name: 'notifications' });
        return permission.state;
      } catch (error) {
        console.warn('[Push Manager] Failed to query permission state:', error);
      }
    }

    // Fallback to Notification.permission
    return Notification.permission;
  }

  /**
   * Request notification permission
   */
  async requestPermission() {
    if (!('Notification' in window)) {
      throw new Error('Notifications are not supported in this browser');
    }

    if (this.permissionState === 'granted') {
      return 'granted';
    }

    try {
      const permission = await Notification.requestPermission();
      this.permissionState = permission;

      console.log('[Push Manager] Permission requested:', permission);
      return permission;
    } catch (error) {
      console.error('[Push Manager] Failed to request permission:', error);
      throw error;
    }
  }

  /**
   * Get existing subscription with better error handling and caching
   */
  async getExistingSubscription() {
    if (!this.registration) {
      return null;
    }

    // Prevent too frequent checks (within 2 seconds)
    const now = Date.now();
    if (now - this.lastSubscriptionCheck < 2000) {
      return this.subscription;
    }
    this.lastSubscriptionCheck = now;

    try {
      const subscription = await this.registration.pushManager.getSubscription();
      this.subscription = subscription;

      if (subscription) {
        console.log('[Push Manager] Found existing subscription');
        // Ensure backend has this subscription stored/active
        try {
          await this.sendSubscriptionToBackend(subscription);
        } catch (syncError) {
          console.warn(
            '[Push Manager] Failed to sync existing subscription to backend:',
            syncError
          );
        }
      } else {
        console.log('[Push Manager] No existing subscription found');
      }

      return subscription;
    } catch (error) {
      console.error('[Push Manager] Failed to get existing subscription:', error);
      return null;
    }
  }

  /**
   * Subscribe to push notifications with duplicate prevention
   */
  async subscribe() {
    if (!this.registration) {
      throw new Error('Service worker not registered');
    }

    if (!this.vapidPublicKey) {
      throw new Error('VAPID public key not loaded');
    }

    if (this.permissionState !== 'granted') {
      throw new Error('Notification permission not granted');
    }

    // Prevent multiple simultaneous subscription attempts
    if (this.subscriptionPromise) {
      console.log('[Push Manager] Subscription already in progress, waiting...');
      return this.subscriptionPromise;
    }

    // Check if already subscribed before attempting to subscribe
    const existingSubscription = await this.getExistingSubscription();
    if (existingSubscription) {
      console.log('[Push Manager] Already subscribed, returning existing subscription');
      return existingSubscription;
    }

    this.subscriptionPromise = this._performSubscription();

    try {
      const result = await this.subscriptionPromise;
      return result;
    } finally {
      this.subscriptionPromise = null;
    }
  }

  /**
   * Internal method to perform the actual subscription
   */
  async _performSubscription() {
    try {
      // Convert VAPID key to Uint8Array
      const vapidKey = this.urlBase64ToUint8Array(this.vapidPublicKey);

      // Subscribe to push notifications
      const subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidKey,
      });

      this.subscription = subscription;
      console.log('[Push Manager] Successfully subscribed to push notifications');

      // Send subscription to backend
      await this.sendSubscriptionToBackend(subscription);

      return subscription;
    } catch (error) {
      console.error('[Push Manager] Failed to subscribe to push notifications:', error);
      throw error;
    }
  }

  /**
   * Unsubscribe from push notifications
   */
  async unsubscribe() {
    if (!this.subscription) {
      console.log('[Push Manager] No active subscription to unsubscribe from');
      return true;
    }

    try {
      const success = await this.subscription.unsubscribe();

      if (success) {
        // Remove subscription from backend
        await this.removeSubscriptionFromBackend();
        this.subscription = null;
        console.log('[Push Manager] Successfully unsubscribed from push notifications');
      }

      return success;
    } catch (error) {
      console.error('[Push Manager] Failed to unsubscribe from push notifications:', error);
      throw error;
    }
  }

  /**
   * Send subscription to backend
   */
  async sendSubscriptionToBackend(subscription) {
    try {
      // Check if user is authenticated before making the API call
      const token = Cookies.get('access_token');
      if (!token) {
        throw new Error('User not authenticated - cannot subscribe to push notifications');
      }

      const subscriptionData = subscription.toJSON();

      // Transform the subscription data to match backend schema
      const backendData = {
        endpoint: subscriptionData.endpoint,
        p256dh_key: subscriptionData.keys.p256dh,
        auth_key: subscriptionData.keys.auth,
        user_agent: navigator.userAgent,
      };

      await notificationsAPI.subscribeToPush(backendData);
      console.log('[Push Manager] Subscription sent to backend');
    } catch (error) {
      console.error('[Push Manager] Failed to send subscription to backend:', error);
      throw error;
    }
  }

  /**
   * Remove subscription from backend
   */
  async removeSubscriptionFromBackend() {
    try {
      await notificationsAPI.unsubscribeFromPush(this.subscription.endpoint);
      console.log('[Push Manager] Subscription removed from backend');
    } catch (error) {
      console.error('[Push Manager] Failed to remove subscription from backend:', error);
      throw error;
    }
  }

  /**
   * Convert base64 VAPID key to Uint8Array
   */
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  /**
   * Test push notification (for development)
   */
  async testNotification() {
    if (this.permissionState !== 'granted') {
      throw new Error('Notification permission not granted');
    }

    try {
      // Try to send a server-side push notification first
      if (this.subscription) {
        const response = await notificationsAPI.testPushNotification();
        console.log('[Push Manager] Server test notification sent:', response);
        return response;
      }
    } catch (error) {
      console.warn(
        '[Push Manager] Server test failed, falling back to browser notification:',
        error
      );
    }

    // Fallback to browser notification
    const notification = new Notification('Test Notification', {
      body: 'This is a test notification from the Rowing Planner app',
      icon: '/icons/icon-192x192.png',
      badge: '/icons/icon-100x100.png',
      tag: 'test-notification',
      requireInteraction: false,
    });

    // Auto-close after 5 seconds
    setTimeout(() => {
      notification.close();
    }, 5000);

    return notification;
  }

  /**
   * Get notification capabilities
   */
  getCapabilities() {
    return {
      supported: 'Notification' in window && 'serviceWorker' in navigator,
      permission: this.permissionState,
      hasSubscription: !!this.subscription,
      hasVapidKey: !!this.vapidPublicKey,
      hasServiceWorker: !!this.registration,
    };
  }

  /**
   * Get subscription status with enhanced validation
   */
  getSubscriptionStatus() {
    return {
      isSubscribed: !!this.subscription,
      isInitialized: this.isInitialized,

      lastCheck: this.lastSubscriptionCheck,
      subscription: this.subscription
        ? {
          endpoint: this.subscription.endpoint,
          expirationTime: this.subscription.expirationTime,
          keys: this.subscription.keys ? Object.keys(this.subscription.keys) : null,
        }
        : null,
    };
  }

  /**
   * Check if subscription is expired
   */
  isSubscriptionExpired(subscription) {
    if (!subscription) return true;

    // Check if subscription has required properties
    if (!subscription.endpoint || !subscription.keys) {
      return true;
    }

    // Check if subscription has expired (if expirationTime is available)
    if (subscription.expirationTime && subscription.expirationTime < Date.now()) {
      return true;
    }

    return false;
  }

  /**
   * Validate subscription with backend
   */
  async validateSubscription(subscription) {
    if (!subscription) return false;

    try {
      // Check if user is authenticated before making the API call
      const token = Cookies.get('access_token');
      if (!token) {
        console.warn('[Push Manager] No auth token found, cannot validate subscription');
        return false;
      }

      // Try to validate with backend by attempting to sync
      await this.sendSubscriptionToBackend(subscription);
      console.log('[Push Manager] Subscription validated with backend');
      return true;
    } catch (error) {
      // If it's a 404 or 410 error, subscription is invalid
      if (error.response?.status === 404 || error.response?.status === 410) {
        console.warn('[Push Manager] Subscription invalid on backend');
        return false;
      }

      // For other errors (network, auth), assume subscription might still be valid
      console.warn('[Push Manager] Could not validate subscription with backend:', error.message);
      return this.isSubscriptionValid(subscription);
    }
  }

  /**
   * Check if subscription appears valid locally
   */
  isSubscriptionValid(subscription) {
    if (!subscription) return false;

    // Check if subscription has required properties
    if (!subscription.endpoint || !subscription.keys) {
      return false;
    }

    // Check if subscription has not expired (if expirationTime is available)
    if (subscription.expirationTime && subscription.expirationTime < Date.now()) {
      return false;
    }

    return true;
  }

  /**
   * Preserve subscription data for recovery after service worker updates
   */
  async preserveSubscriptionForRecovery() {
    if (!this.subscription) return;

    try {
      const subscriptionData = {
        endpoint: this.subscription.endpoint,
        keys: this.subscription.keys ? {
          p256dh: this.subscription.keys.p256dh,
          auth: this.subscription.keys.auth
        } : null,
        timestamp: Date.now()
      };

      // Store in localStorage for recovery
      localStorage.setItem('push-subscription-backup', JSON.stringify(subscriptionData));
      console.log('[Push Manager] Subscription preserved for recovery');
    } catch (error) {
      console.warn('[Push Manager] Failed to preserve subscription:', error);
    }
  }

  /**
   * Recover subscription from backup after service worker updates
   */
  async recoverSubscriptionFromBackup() {
    try {
      const backupData = localStorage.getItem('push-subscription-backup');
      if (!backupData) return false;

      const subscriptionData = JSON.parse(backupData);
      const backupAge = Date.now() - subscriptionData.timestamp;

      // Only use backup if it's less than 24 hours old
      if (backupAge > 24 * 60 * 60 * 1000) {
        console.log('[Push Manager] Subscription backup too old, removing');
        localStorage.removeItem('push-subscription-backup');
        return false;
      }

      // Check if current subscription matches backup
      if (this.subscription && this.subscription.endpoint === subscriptionData.endpoint) {
        console.log('[Push Manager] Current subscription matches backup, no recovery needed');
        return true;
      }

      // Try to validate the backup subscription
      const isValid = await this.validateSubscriptionFromData(subscriptionData);
      if (isValid) {
        console.log('[Push Manager] Backup subscription is valid, will attempt recovery');
        return true;
      } else {
        console.log('[Push Manager] Backup subscription is invalid, removing');
        localStorage.removeItem('push-subscription-backup');
        return false;
      }
    } catch (error) {
      console.warn('[Push Manager] Failed to recover subscription from backup:', error);
      return false;
    }
  }

  /**
   * Validate subscription data without actual subscription object
   */
  async validateSubscriptionFromData(subscriptionData) {
    try {
      // Check if user is authenticated
      const token = Cookies.get('access_token');
      if (!token) return false;

      // Create a mock subscription object for validation
      const mockSubscription = {
        endpoint: subscriptionData.endpoint,
        keys: subscriptionData.keys,
        toJSON: () => ({
          endpoint: subscriptionData.endpoint,
          keys: subscriptionData.keys
        })
      };

      // Try to validate with backend
      await this.sendSubscriptionToBackend(mockSubscription);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Clean up subscription backup after successful recovery
   */
  clearSubscriptionBackup() {
    try {
      localStorage.removeItem('push-subscription-backup');
      console.log('[Push Manager] Subscription backup cleared');
    } catch (error) {
      console.warn('[Push Manager] Failed to clear subscription backup:', error);
    }
  }

  /**
   * Debug method to get detailed subscription information
   */
  getDebugInfo() {
    return {
      isInitialized: this.isInitialized,
      hasRegistration: !!this.registration,
      hasVapidKey: !!this.vapidPublicKey,
      permission: this.permissionState,
      subscription: this.subscription
        ? {
          endpoint: this.subscription.endpoint,
          expirationTime: this.subscription.expirationTime,
          keys: this.subscription.keys ? Object.keys(this.subscription.keys) : null,
        }
        : null,
      lastSubscriptionCheck: this.lastSubscriptionCheck,
      subscriptionPromise: !!this.subscriptionPromise,
    };
  }
}

// Create singleton instance
const pushManager = new PushNotificationManager();

export default pushManager;
